import {
  castArray,
  chunk,
  cloneDeep,
  compact,
  isArray,
  isEmpty,
  isEqual,
  sortBy,
} from 'lodash-es';
import { FormikErrors } from 'formik';

import {
  AudienceGroup,
  AudienceGroupOption,
  AudienceLogicalModifier,
  Country,
  PanelProvider,
  Question,
  QuestionOption,
  Survey,
  SurveyAudienceBody,
  SurveyAudienceType,
  SurveyAudienceTypeBucket,
  SurveyQualityCheck,
  TargetRangeConstraint,
} from '../types/domainModels';
import { CreateAudienceBody } from '../services/backend/audience';
import { getQuestionOption } from './formOptions';
import { ReactSelectValue } from '../types/forms';
import { UpdateInboundCensusBody } from '../services/backend/surveys';
import { VALUE_FOR_US_COUNTRY } from 'components/surveyEdit/AudienceProfile/AudienceProfile';

export type AudienceSource = 'byoa' | 'sample-provider';
export type AudienceTypeOptions = 'custom' | 'natural';

export enum DEMOGRAPHIC_QUOTA_TYPE {
  ENUM = 2,
  OPEN_END = 3,
  RANGE = 10,
}

export const AUDIENCE_TARGET_TYPE = {
  RANGE: 2, // age
  OPEN_END: 10, // zipcode
} as const;

export const logicalModifierOptionsForTargetEnum = [
  { label: 'are', value: 'is' },
  { label: 'are not', value: 'isnt' },
  { label: 'are either', value: 'should' },
];

export const logicalModifierOptionsForTargetRange = [
  { label: 'are between', value: 'is' },
  { label: 'are not between', value: 'isnt' },
  { label: 'are either', value: 'should' },
];

export type DemographicConstraint =
  | QuestionOption
  | {
      end: number | '';
      start: number | '';
    };
export type DemographicConstraintValidated =
  | QuestionOption
  | {
      end: number;
      start: number;
    };

export interface DemographicFormData {
  audienceProfile: {
    audienceSource: AudienceSource;
    audienceTargets: SurveyAudienceTypeBucket[];
    audienceType: AudienceTypeOptions;
    demographicQuotas: DemographicQuota[];
    inboundCensus: {
      useAge: boolean;
      useEthnicity: boolean;
      useGender: boolean;
      useRace: boolean;
      useRegion: boolean;
      useHHI: boolean;
    };
    panelProvider: PanelProvider | null;
    title: string;
  };
  respondentQuality: {
    qualityChecks: {
      bad_respondent: boolean;
      copy_paste: boolean;
      duplicate: boolean;
      presurvey_questionnaire: boolean;
    };
  };
  countryListOptions: ReactSelectValue<string>[];
  selectedCountry: string | null;
}

export interface DemographicFormDataValidated {
  audienceProfile: {
    audienceSource: AudienceSource;
    audienceTargets: SurveyAudienceTypeBucket[];
    audienceType: AudienceTypeOptions;
    demographicQuotas: {
      demographic: ReactSelectValue<Question>;
      quotas: {
        constraints: QuotaConstraintsValidated;
        logicalModifier: ReactSelectValue<AudienceLogicalModifier>;
        percentage: number;
      }[];
    }[];
    inboundCensus: {
      useAge: boolean;
      useEthnicity: boolean;
      useGender: boolean;
      useRace: boolean;
      useRegion: boolean;
      useHHI: boolean;
    };
    panelProvider: PanelProvider | null;
    title: string;
  };
  respondentQuality: {
    qualityChecks: {
      bad_respondent: boolean;
      copy_paste: boolean;
      duplicate: boolean;
      presurvey_questionnaire: boolean;
    };
  };
  countryListOptions: ReactSelectValue<string>[];
  selectedCountry: string | null;
}

export interface DemographicQuota {
  demographic: ReactSelectValue<Question> | null;
  quotas: Quota[];
}

export const AUDIENCE_SOURCE_OPTIONS = [
  { label: 'Glass Survey Audience', value: 'sample-provider' },
  { label: 'My Own Audience', value: 'byoa' },
] satisfies ReactSelectValue<AudienceSource>[];

export const LOGICAL_MODIFIER_OPTIONS_MAP: {
  [id in DEMOGRAPHIC_QUOTA_TYPE]: ReactSelectValue<AudienceLogicalModifier>[];
} = {
  [DEMOGRAPHIC_QUOTA_TYPE.ENUM]: [
    { label: 'are', value: 'is' },
    { label: 'are not', value: 'isnt' },
    { label: 'are either', value: 'should' },
    // { label: 'are not any of', value: 'shouldnt' },
  ],
  [DEMOGRAPHIC_QUOTA_TYPE.RANGE]: [
    { label: 'are between', value: 'is' },
    { label: 'are not between', value: 'isnt' },
    { label: 'are between either', value: 'should' },
    // { label: 'are not between any of', value: 'shouldnt' },
  ],
  [DEMOGRAPHIC_QUOTA_TYPE.OPEN_END]: [
    { label: 'are between', value: 'is' },
    { label: 'are not between', value: 'isnt' },
    { label: 'are between either', value: 'should' },
    // { label: 'are not between any of', value: 'shouldnt' },
  ],
};

export interface Quota {
  constraints: QuotaConstraints;
  logicalModifier: ReactSelectValue<AudienceLogicalModifier> | null;
  percentage: number | '';
}

export type QuotaConstraints =
  | ReactSelectValue<DemographicConstraint>
  | (DemographicConstraint | ReactSelectValue<DemographicConstraint>)[];
export type QuotaConstraintsValidated =
  | ReactSelectValue<DemographicConstraintValidated>
  | (
      | DemographicConstraintValidated
      | ReactSelectValue<DemographicConstraintValidated>
    )[];

export function apiDataToFormData({
  audienceGroupOptionsByGroupId,
  audienceTargets,
  countryListOptions,
  demographicQuestions,
  survey,
}: {
  audienceGroupOptionsByGroupId: Map<number, AudienceGroupOption[]>;
  audienceTargets: SurveyAudienceBody[];
  countryListOptions: ReactSelectValue<string>[];
  demographicQuestions: Question[];
  survey: Survey;
}): DemographicFormData {
  const audienceType = survey.audienceId === 0 ? 'natural' : 'custom';
  let demographicQuotas = [getEmptyDemographicQuota()];
  let title = '';

  if (audienceType === 'custom') {
    title = survey.audience.title;
    demographicQuotas = survey.audience.audienceSlices.map((slice) => {
      const demographicQuestion =
        demographicQuestions.find(({ id }) => {
          return id === slice.audienceSliceCategories[0]?.questionId;
        }) ?? null;
      const modifierOptions = demographicQuestion
        ? getLogicalModifierOptions(demographicQuestion)
        : [];

      return {
        demographic: demographicQuestion
          ? getQuestionOption({ question: demographicQuestion })
          : null,
        quotas: slice.audienceSliceCategories.map((category) => {
          const constraints = compact(
            category.audienceSliceCategoryAttributes.map(
              ({ audienceAttribute }) => {
                if (audienceAttribute.enumValueId) {
                  const option = demographicQuestion?.options.find(({ id }) => {
                    return id === audienceAttribute.enumValueId;
                  });
                  if (!option) {
                    return null;
                  }

                  return { label: option.title, value: option };
                }

                if (audienceAttribute.numberRange) {
                  return {
                    end: audienceAttribute.numberRange.end,
                    start: audienceAttribute.numberRange.start,
                  };
                }

                return null;
              },
            ),
          );

          return {
            constraints,
            logicalModifier:
              modifierOptions.find(({ value }) => {
                return value === category.logicalModifier;
              }) ?? null,
            percentage: Number(category.percentage) * 100,
          };
        }),
      };
    });
  }

  return {
    audienceProfile: {
      audienceSource: survey.isBringYourOwnAudience
        ? 'byoa'
        : 'sample-provider',
      audienceTargets:
        audienceTargets.length > 0
          ? convertSurveyAudienceTypeResponseToParseableAudience(
              audienceTargets,
              audienceGroupOptionsByGroupId,
            )
          : [getEmptyAudience()],
      audienceType,
      demographicQuotas,
      inboundCensus: {
        useAge: survey.useAgeCensus,
        useEthnicity: survey.useEthnicityCensus,
        useGender: survey.useGenderCensus,
        useRace: survey.useRaceCensus,
        useRegion: survey.useRegionCensus,
        useHHI: survey.useHHICensus,
      },
      panelProvider: survey.isBringYourOwnAudience
        ? null
        : (survey.panelProvider ?? 'LUCID'),
      title,
    },
    respondentQuality: {
      qualityChecks: {
        bad_respondent: getInitialQualityCheck({
          qualityChecks: survey.qualityChecks,
          type: 'bad_respondent',
        }),
        copy_paste: getInitialQualityCheck({
          qualityChecks: survey.qualityChecks,
          type: 'copy_paste',
        }),
        duplicate: getInitialQualityCheck({
          qualityChecks: survey.qualityChecks,
          type: 'duplicate',
        }),
        presurvey_questionnaire: getInitialQualityCheck({
          qualityChecks: survey.qualityChecks,
          type: 'presurvey_questionnaire',
        }),
      },
    },
    countryListOptions,
    selectedCountry: survey.selectedCountry ?? VALUE_FOR_US_COUNTRY,
  };
}

export function formDataToApiData({
  formData,
}: {
  formData: DemographicFormDataValidated;
}): CreateAudienceBody {
  const { demographicQuotas, title } = formData.audienceProfile;

  const audienceSlices = demographicQuotas.map(({ demographic, quotas }) => {
    return {
      audienceSliceCategories: quotas.map((quota) => {
        const constraints = isArray(quota.constraints)
          ? quota.constraints
          : [quota.constraints];

        return {
          audienceSliceCategoryAttributes: constraints.map((constraint) => {
            const constraintValue =
              'value' in constraint ? constraint.value : constraint;

            if ('id' in constraintValue) {
              return constraintValue.id;
            }

            return constraintValue;
          }),
          logicalModifier: quota.logicalModifier.value,
          percentage: Math.round(quota.percentage * 100000) / 10000000,
          question: demographic.value,
          questionId: demographic.value.id,
        };
      }),
      percentage: 1,
    };
  });

  return { audienceSlices, title };
}

export function formInboundCensusToApiData({
  formData,
}: {
  formData: DemographicFormDataValidated;
}): UpdateInboundCensusBody {
  const {
    useAge: useAgeCensus,
    useEthnicity: useEthnicityCensus,
    useGender: useGenderCensus,
    useRace: useRaceCensus,
    useRegion: useRegionCensus,
    useHHI: useHHICensus,
  } = formData.audienceProfile.inboundCensus;

  return {
    useAgeCensus,
    useEthnicityCensus,
    useGenderCensus,
    useRaceCensus,
    useRegionCensus,
    useHHICensus,
  };
}

export function getAudienceGroup({
  group,
}: {
  group: AudienceGroup;
}): ReactSelectValue<number> {
  return {
    label: group.name,
    value: group.id,
  };
}

export function getAudienceGroups({
  groups,
  allSelectedAudienceGroups,
  thisSelectedAudienceGroupId,
}: {
  groups: AudienceGroup[];
  allSelectedAudienceGroups: SurveyAudienceTypeBucket[];
  thisSelectedAudienceGroupId: number | null;
}): ReactSelectValue<number>[] {
  // this first sorts, then filters out all already selected options except the
  // element's selected option, then converts the rest to the formik format
  return sortBy(groups, (group) => group.name)
    .filter(
      (group) =>
        !allSelectedAudienceGroups.some(
          (selectedGroup) => selectedGroup.audienceGroupId === group.id,
        ) || group.id === thisSelectedAudienceGroupId,
    )
    .map((group) => {
      return getAudienceGroup({ group });
    });
}

export function getInitialQualityCheck({
  qualityChecks,
  type,
}: {
  qualityChecks: SurveyQualityCheck[];
  type: SurveyQualityCheck['type'];
}) {
  return (
    qualityChecks.find((qualityCheck) => qualityCheck.type === type)?.enabled ??
    true
  );
}

export function getEmptyAudience(): SurveyAudienceTypeBucket {
  return {
    audienceGroupId: null,
    surveyAudienceTypes: [getEmptyAudienceTarget()],
  };
}

export function getEmptyAudienceTarget(
  audienceGroupId?: number | null,
): SurveyAudienceType {
  return {
    audienceGroupId: audienceGroupId || 0,
    audienceGroupOptionSelections: [],
    id: 0,
    logicalModifier: '',
    numberRange: [getEmptyQuotaRangeConstraint()],
    numberSelections: '',
    percentage: null,
    surveyId: 0,
  };
}

export function pairifyNumberRange(
  numberRange: number[],
): TargetRangeConstraint[] {
  return chunk(numberRange, 2)
    .filter((pair): pair is [number, number] => pair.length === 2)
    .map(([start, end]) => ({ start: String(start), end: String(end) }));
}

export function convertSurveyAudienceTypeResponseToParseableAudience(
  response: SurveyAudienceBody[],
  audienceGroupOptionsByGroupId: Map<number, AudienceGroupOption[]>,
): SurveyAudienceTypeBucket[] {
  const parsedResponse: SurveyAudienceTypeBucket[] = [];
  response.map((audience: SurveyAudienceBody) => {
    const logicalModifierOption = (
      audience.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE
        ? logicalModifierOptionsForTargetRange
        : logicalModifierOptionsForTargetEnum
    ).find(({ value }) => value === audience.logicalModifier);
    const audienceGroupOptions =
      audienceGroupOptionsByGroupId.get(audience.audienceGroupId) ?? [];
    const audienceGroupOptionSelections = audience.audienceGroupOptionSelections
      .map((optionId) => {
        return audienceGroupOptions.find(({ id }) => id === optionId);
      })
      .filter((option): option is AudienceGroupOption => !!option)
      .map((option) => {
        return { label: option.name, value: option };
      });
    const numberRange = pairifyNumberRange(audience.numberRange);
    const parsedAudience: SurveyAudienceType = {
      ...audience,
      audienceGroupOptionSelections:
        audienceGroupOptionSelections as unknown as number[],
      logicalModifier: (logicalModifierOption ??
        audience.logicalModifier) as unknown as string,
      numberRange,
      numberSelections: audience.numberSelections.join(' '),
      percentage: audience.percentage && audience.percentage * 100,
      questionQuotaId: audience.questionQuotaId,
    };
    const foundIndex: number = parsedResponse.findIndex(
      (e) => e.audienceGroupId === audience.audienceGroupId,
    );
    if (foundIndex > -1) {
      parsedResponse[foundIndex].surveyAudienceTypes.push(parsedAudience);
    } else {
      parsedResponse.push({
        audienceGroupId: audience.audienceGroupId,
        surveyAudienceTypes: [parsedAudience],
      });
    }
  });
  return parsedResponse;
}

function getSelectedOptionIds(
  selections:
    | number[]
    | ReactSelectValue<AudienceGroupOption>
    | ReactSelectValue<AudienceGroupOption>[],
): number[] {
  const values = Array.isArray(selections) ? selections : [selections];

  return values.map((selection) => {
    return typeof selection === 'number' ? selection : selection.value.id;
  });
}

function getLogicalModifierValue(
  logicalModifier: string | ReactSelectValue<AudienceLogicalModifier>,
): string {
  return typeof logicalModifier === 'string'
    ? logicalModifier
    : logicalModifier.value;
}

function getLogicalModifierLabel(
  logicalModifier: string | ReactSelectValue<AudienceLogicalModifier>,
): string {
  return typeof logicalModifier === 'string'
    ? logicalModifier
    : logicalModifier.label;
}

function getSelectionLabels(
  selections:
    | number[]
    | ReactSelectValue<AudienceGroupOption>
    | ReactSelectValue<AudienceGroupOption>[],
  audienceGroupOptions: AudienceGroupOption[],
): string[] {
  const values = Array.isArray(selections) ? selections : [selections];

  return compact(
    values.map((selection) => {
      if (typeof selection === 'number') {
        return (
          audienceGroupOptions.find(({ id }) => id === selection)?.name ?? null
        );
      }

      return selection.label;
    }),
  );
}

export function audienceTargetsFormDataToApiData({
  audienceTargets,
  surveyId,
}: {
  audienceTargets: SurveyAudienceTypeBucket[];
  surveyId: number;
}): SurveyAudienceBody[] {
  return audienceTargets.flatMap(({ audienceGroupId, surveyAudienceTypes }) => {
    if (audienceGroupId === null) {
      return [];
    }

    return surveyAudienceTypes.map((target) => {
      return {
        audienceGroupId,
        audienceGroupOptionSelections:
          audienceGroupId === AUDIENCE_TARGET_TYPE.OPEN_END ||
          audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE
            ? []
            : getSelectedOptionIds(target.audienceGroupOptionSelections),
        id: target.id,
        logicalModifier: getLogicalModifierValue(target.logicalModifier),
        numberRange:
          audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE
            ? target.numberRange.flatMap(({ start, end }) => [
                Number(start),
                Number(end),
              ])
            : [],
        numberSelections:
          audienceGroupId === AUDIENCE_TARGET_TYPE.OPEN_END
            ? target.numberSelections
                .split(' ')
                .filter((value) => value !== '')
                .map(Number)
            : [],
        percentage: target.percentage && target.percentage / 100,
        surveyId,
      };
    });
  });
}

// AudienceGroup.name for each census category, used to match a checked inbound-census option
// against a manually-specified audience target for the same demographic (GLA-3002). Matching on
// name (rather than id) is self-documenting and mirrors the backend's equivalent lookup in
// `censusAudienceGroups.ts`, which keys off the AudienceGroup's Lucid question name instead.
const CENSUS_AUDIENCE_GROUP_NAMES: Record<
  keyof DemographicFormData['audienceProfile']['inboundCensus'],
  string
> = {
  useAge: 'Age',
  useEthnicity: 'Latinx',
  useGender: 'Gender Identity',
  useHHI: 'Household Income',
  useRace: 'Race',
  useRegion: 'Region',
};

export function getInboundCensusAudienceTargetConflicts({
  audienceGroups,
  formData,
}: {
  audienceGroups: AudienceGroup[];
  formData: DemographicFormData;
}): string[] {
  const targetedAudienceGroupIds = new Set(
    formData.audienceProfile.audienceTargets.map(
      (bucket) => bucket.audienceGroupId,
    ),
  );

  const conflicts: string[] = [];
  for (const [inboundCensusKey, groupName] of Object.entries(
    CENSUS_AUDIENCE_GROUP_NAMES,
  ) as [
    keyof DemographicFormData['audienceProfile']['inboundCensus'],
    string,
  ][]) {
    if (!formData.audienceProfile.inboundCensus[inboundCensusKey]) {
      continue;
    }

    const group = audienceGroups.find(
      (audienceGroup) =>
        audienceGroup.isCensusOption && audienceGroup.name === groupName,
    );

    if (group && targetedAudienceGroupIds.has(group.id)) {
      conflicts.push(`"${groupName}"`);
    }
  }

  return conflicts;
}

export function validateAudienceTargetFormData(
  formData: DemographicFormData,
  audienceGroups: AudienceGroup[] = [],
): FormikErrors<DemographicFormData> {
  if (formData.audienceProfile.audienceType !== 'custom') {
    return {};
  }

  const errors: FormikErrors<DemographicFormData> = {};

  const inboundCensusConflicts = getInboundCensusAudienceTargetConflicts({
    audienceGroups,
    formData,
  });
  if (inboundCensusConflicts.length > 0) {
    errors.audienceProfile = {
      ...errors.audienceProfile,
      inboundCensus: {
        _error: `You cannot use both Inbound Census ${inboundCensusConflicts.join(', ')} and matching Audience Target demographics together. Please choose one option.`,
      } as FormikErrors<
        DemographicFormData['audienceProfile']['inboundCensus']
      >,
    };
  }

  const audienceTargetsErrors: FormikErrors<SurveyAudienceTypeBucket>[] = [];

  formData.audienceProfile.audienceTargets.forEach((bucket) => {
    if (bucket.audienceGroupId === null) {
      audienceTargetsErrors.push({});
      return;
    }

    const targetErrors: FormikErrors<SurveyAudienceType>[] = [];

    bucket.surveyAudienceTypes.forEach((target) => {
      const errors: FormikErrors<SurveyAudienceType> = {};

      if (bucket.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE) {
        if (target.numberRange.length === 0) {
          // Formik doesn't like this specification for the error but it's perfectly acceptable.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errors.numberRange = 'Required' as any;
        } else {
          const numberRangeErrors = target.numberRange.map((range) => {
            const rangeErrors: FormikErrors<{ end: string; start: string }> =
              {};

            if (range.start === '') {
              rangeErrors.start = 'Required';
            } else if (Number(range.start) <= 0) {
              rangeErrors.start = 'Must be positive';
            } else if (Number(range.start) < 18) {
              rangeErrors.start = 'Must be 18 or older.';
            }

            if (range.end === '') {
              rangeErrors.end = 'Required';
            } else if (Number(range.end) <= 0) {
              rangeErrors.end = 'Must be positive';
            } else if (Number(range.end) < 18) {
              rangeErrors.end = 'Must be 18 or older.';
            } else if (
              range.start !== '' &&
              Number(range.end) < Number(range.start)
            ) {
              rangeErrors.end = 'Must be greater than the starting age.';
            }

            return rangeErrors;
          });

          if (numberRangeErrors.some((rangeErrors) => !isEmpty(rangeErrors))) {
            errors.numberRange = numberRangeErrors;
          }
        }
      } else if (bucket.audienceGroupId === AUDIENCE_TARGET_TYPE.OPEN_END) {
        const numberSelectionValues = target.numberSelections.split(' ');
        const nonEmptyValues = numberSelectionValues.filter(
          (value) => value !== '',
        );

        if (nonEmptyValues.length === 0) {
          // Formik doesn't like this specification for the error but it's perfectly acceptable.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errors.numberSelections = 'Required' as any;
        } else if (nonEmptyValues.some((value) => !/^\d{5}$/.test(value))) {
          // Formik doesn't like this specification for the error but it's perfectly acceptable.
          errors.numberSelections = // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'Zipcode must be exactly 5 digits and separated by a single space' as any;
        }
      } else if (
        getSelectedOptionIds(target.audienceGroupOptionSelections).length === 0
      ) {
        // Formik doesn't like this specification for the error but it's perfectly acceptable.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errors.audienceGroupOptionSelections = 'Required' as any;
      }

      if (!getLogicalModifierValue(target.logicalModifier)) {
        errors.logicalModifier = 'Required';
      }

      if (!target.percentage) {
        errors.percentage = 'Required';
      }

      targetErrors.push(errors);
    });

    const bucketErrors: FormikErrors<SurveyAudienceTypeBucket> = {};

    if (targetErrors.some((errors) => !isEmpty(errors))) {
      bucketErrors.surveyAudienceTypes = targetErrors;
    } else {
      const percentageSum = bucket.surveyAudienceTypes.reduce((sum, target) => {
        return sum + (target.percentage || 0);
      }, 0);

      if (Math.abs(percentageSum - 100) > 0.01) {
        bucketErrors.surveyAudienceTypes = `Percentages must add up to 100%. ${
          100 - percentageSum
        }% left.`;
      }
    }

    audienceTargetsErrors.push(bucketErrors);
  });

  if (audienceTargetsErrors.some((targetErrors) => !isEmpty(targetErrors))) {
    errors.audienceProfile = {
      ...errors.audienceProfile,
      audienceTargets: audienceTargetsErrors,
    };
  }

  return errors;
}

export interface QuotaEligibleTarget {
  target: Omit<
    SurveyAudienceType,
    'audienceGroupOptionSelections' | 'logicalModifier'
  > & {
    audienceGroupOptionSelections: string;
    logicalModifier: string;
    id: number;
  };
}

export function getTargetsNeedingQuotas(
  audienceTargets: SurveyAudienceTypeBucket[],
  audienceGroupOptionsByGroupId: Map<number, AudienceGroupOption[]>,
  previousAudienceTargets?: SurveyAudienceBody[],
): QuotaEligibleTarget[] {
  return audienceTargets.flatMap((bucket) => {
    if (
      bucket.audienceGroupId === null ||
      bucket.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE ||
      bucket.audienceGroupId === AUDIENCE_TARGET_TYPE.OPEN_END ||
      bucket.audienceGroupId === 11 // State
    ) {
      return [];
    }

    const audienceGroupOptions =
      audienceGroupOptionsByGroupId.get(bucket.audienceGroupId) ?? [];

    return bucket.surveyAudienceTypes
      .filter((target) => {
        if (target.percentage !== 100) {
          return false;
        }

        if (!target.questionQuotaId) {
          return true;
        }

        if (!previousAudienceTargets) {
          return false;
        }

        const currentSelectionsById = (
          target.audienceGroupOptionSelections as unknown as ReactSelectValue<AudienceGroupOption>[]
        ).map((t) => t.value.id);
        const previousTarget = previousAudienceTargets.find(
          (prev) => prev.id === target.id,
        );

        return !isEqual(
          currentSelectionsById,
          previousTarget?.audienceGroupOptionSelections,
        );
      })
      .map((target) => ({
        target: {
          ...target,
          audienceGroupOptionSelections: getSelectionLabels(
            target.audienceGroupOptionSelections as unknown as
              | number[]
              | ReactSelectValue<AudienceGroupOption>
              | ReactSelectValue<AudienceGroupOption>[],
            audienceGroupOptions,
          ).join(', '),
          logicalModifier: getLogicalModifierLabel(
            target.logicalModifier as unknown as
              | string
              | ReactSelectValue<AudienceLogicalModifier>,
          ),
        },
      }));
  });
}

export function getEmptyDemographicQuota(
  demographic?: Question | null,
): DemographicQuota {
  return {
    demographic: null,
    quotas: [getEmptyQuota(demographic)],
  };
}

export function getEmptyQuota(demographic?: Question | null): Quota {
  const constraints =
    demographic?.questionTypeId === DEMOGRAPHIC_QUOTA_TYPE.RANGE
      ? [getEmptyQuotaRangeConstraint()]
      : [];
  const logicalModifierOptions = demographic
    ? getLogicalModifierOptions(demographic)
    : [];

  return {
    constraints,
    logicalModifier:
      logicalModifierOptions.length > 0 ? logicalModifierOptions[0] : null,
    percentage: '',
  };
}

export function getEmptyQuotaRangeConstraint(): { end: ''; start: '' } {
  return { end: '', start: '' };
}

export function getLogicalModifierOptions(
  demographic: Question,
): ReactSelectValue<AudienceLogicalModifier>[] {
  // TODO: Address compilation error
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return LOGICAL_MODIFIER_OPTIONS_MAP[demographic.questionTypeId] || [];
}

export function getValuesForAudienceTypeChange({
  formData,
  newAudienceType,
}: {
  formData: DemographicFormData;
  newAudienceType: DemographicFormData['audienceProfile']['audienceType'];
}): DemographicFormData {
  const newFormData = cloneDeep(formData);
  newFormData.audienceProfile.audienceType = newAudienceType;

  if (newAudienceType === 'natural') {
    newFormData.audienceProfile.demographicQuotas = [
      getEmptyDemographicQuota(),
    ];
    newFormData.audienceProfile.title = '';
  }

  return newFormData;
}

export function getValuesForAudienceSourceChange({
  formData,
  newAudienceSource,
}: {
  formData: DemographicFormData;
  newAudienceSource: DemographicFormData['audienceProfile']['audienceSource'];
}): DemographicFormData {
  const newFormData = cloneDeep(formData);
  newFormData.audienceProfile.audienceSource = newAudienceSource;

  // Inbound census controls are hidden (not unmounted-and-reset) while BYOA
  // is selected, so a stale checked census box can silently persist and
  // later conflict with manually-specified quotas once a sample-provider
  // audience is chosen again (GLA-3002).
  if (newAudienceSource === 'byoa') {
    newFormData.audienceProfile.inboundCensus = {
      useAge: false,
      useEthnicity: false,
      useGender: false,
      useHHI: false,
      useRace: false,
      useRegion: false,
    };
  }

  return newFormData;
}

export function isMultiSelectLogicalModifier(
  logicalModifier: AudienceLogicalModifier,
): boolean {
  return logicalModifier === 'should' || logicalModifier === 'shouldnt';
}

export function validateDemographicFormData(
  formData: DemographicFormData,
): FormikErrors<DemographicFormData> {
  if (formData.audienceProfile.audienceType !== 'custom') {
    return {};
  }

  const errors: FormikErrors<DemographicFormData> = {};
  if (!formData.audienceProfile.title) {
    errors.audienceProfile = {
      ...errors.audienceProfile,
      title: 'Please provide a title',
    };
  }

  // Check for conflicts between inbound census and demographic quotas.
  // If the inbound census is enabled, then the demographic quotas must not include the same demographic.
  // As it will might create conflicting quotas in Lucid.
  const inboundCensusConflicts: string[] = [];
  const processedCombinations = new Set<string>();

  formData.audienceProfile.demographicQuotas.forEach(
    (demographicQuota, quotaIndex) => {
      if (demographicQuota.demographic?.value) {
        const questionLabel = demographicQuota.demographic.value.label;

        if (questionLabel) {
          // Check each demographic label against inbound census options
          for (const [inboundLabel, inboundField] of Object.entries({
            Age: { inboundFieldName: 'useAge', inboundLabel: 'Age' },
            Gender: { inboundFieldName: 'useGender', inboundLabel: 'Gender' },
            Race: { inboundFieldName: 'useRace', inboundLabel: 'Race' },
            Ethnicity: {
              inboundFieldName: 'useEthnicity',
              inboundLabel: 'Hispanic',
            },
            Region: { inboundFieldName: 'useRegion', inboundLabel: 'Region' },
            'Annual Income': {
              inboundFieldName: 'useHHI',
              inboundLabel: 'Household Income',
            },
          })) {
            const isLabelMatch =
              questionLabel.includes(inboundLabel) ||
              questionLabel.startsWith(inboundLabel);

            if (isLabelMatch) {
              const inboundCensusKey =
                inboundField.inboundFieldName as keyof typeof formData.audienceProfile.inboundCensus;
              const isInboundChecked =
                formData.audienceProfile.inboundCensus[inboundCensusKey];

              // Create a unique key for this demographic + inbound census combination
              const combinationKey = `${inboundCensusKey}-${quotaIndex}-${demographicQuota.demographic.value.id}`;
              if (
                isInboundChecked &&
                !processedCombinations.has(combinationKey)
              ) {
                inboundCensusConflicts.push(`"${inboundField.inboundLabel}"`);
                processedCombinations.add(combinationKey);
              }
            }
          }
        }
      }
    },
  );

  if (inboundCensusConflicts.length > 0) {
    errors.audienceProfile = {
      ...errors.audienceProfile,
      inboundCensus: {
        _error: `You cannot use both Inbound Census ${inboundCensusConflicts.join(', ')} and matching Audience Quota demographics together. Please choose one option.`,
      } as FormikErrors<
        DemographicFormData['audienceProfile']['inboundCensus']
      >,
    };
  }

  const demographicQuotasErrors: FormikErrors<DemographicQuota>[] = [];
  formData.audienceProfile.demographicQuotas.forEach((demographicQuota) => {
    const demographicQuotaErrors: FormikErrors<DemographicQuota> = {};

    if (!demographicQuota.demographic) {
      demographicQuotaErrors.demographic = 'Please choose a demographic.';
    }

    const quotasErrors: FormikErrors<Quota>[] = [];
    demographicQuota.quotas.forEach((quota) => {
      const quotaErrors: FormikErrors<Quota> = {};

      if (castArray(quota.constraints).length === 0) {
        // Formik doesn't like this specification for the error but it's perfectly acceptable.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        quotaErrors.constraints = 'Required' as any;
      }

      const constraintsErrors: FormikErrors<Quota['constraints']> = [];
      castArray(quota.constraints).forEach((constraint) => {
        const constraintErrors: FormikErrors<{ end: string; start: string }> =
          {};

        if ('end' in constraint) {
          if (constraint.end === '') {
            constraintErrors.end = 'Required';
          } else if (constraint.end <= 0) {
            constraintErrors.end = 'Must be positive';
          } else if (constraint.end <= 12) {
            constraintErrors.end = 'Must be 13 or older.';
          }

          if (constraint.start === '') {
            constraintErrors.start = 'Required';
          } else if (constraint.start <= 0) {
            constraintErrors.start = 'Must be positive';
          } else if (constraint.start <= 12) {
            constraintErrors.start = 'Must be 13 or older.';
          }
        }

        constraintsErrors.push(constraintErrors);
      });

      if (constraintsErrors.some((errors) => !isEmpty(errors))) {
        quotaErrors.constraints = constraintsErrors;
      }

      if (!quota.logicalModifier) {
        quotaErrors.logicalModifier = 'Required';
      }

      if (!quota.percentage) {
        quotaErrors.percentage = 'Required';
      }

      quotasErrors.push(quotaErrors);
    });

    const quotasSum = demographicQuota.quotas.reduce((currentSum, quota) => {
      return currentSum + (quota.percentage || 0);
    }, 0);

    if (quotasErrors.some((errors) => !isEmpty(errors))) {
      demographicQuotaErrors.quotas = quotasErrors;
    } else if (quotasSum !== 100) {
      demographicQuotaErrors.quotas = `Percentages must add up to 100%. ${
        100 - quotasSum
      }% left.`;
    }

    demographicQuotasErrors.push(demographicQuotaErrors);
  });

  if (demographicQuotasErrors.some((errors) => !isEmpty(errors))) {
    errors.audienceProfile = {
      ...errors.audienceProfile,
      demographicQuotas: demographicQuotasErrors,
    };
  }

  return errors;
}

/**
 * Returns an array of inactive demographic questions that are used in audience quotas.
 */
export function getInactiveDemographicQuestionsInQuotas({
  demographicQuestions,
  survey,
}: {
  demographicQuestions: Question[];
  survey: Survey;
}): Question[] {
  // If this is a natural fallout survey or doesn't have a custom audience, return empty array
  if (survey.audienceId === 0 || !survey.audience) {
    return [];
  }

  // Extract question IDs from the audience slices
  const quotaQuestionIds = new Set<number>();
  survey.audience.audienceSlices.forEach((slice) => {
    slice.audienceSliceCategories.forEach((category) => {
      if (category.questionId && category.question.isDemographic) {
        quotaQuestionIds.add(category.questionId);
      }
    });
  });

  // Return inactive demographic questions that are used in quotas
  return demographicQuestions.filter(
    (question) =>
      question.isDemographic &&
      !question.isActive &&
      quotaQuestionIds.has(question.id),
  );
}

export function sortAndFormatCountryList(unsortedCountries: Country[]) {
  return unsortedCountries
    .map((country): ReactSelectValue<string> => {
      return { label: country.countryName, value: country.countryCode };
    })
    .sort((a, b) => {
      if (a.label === b.label) {
        return 0;
      }
      if (a.label === 'United States') {
        return -1;
      }
      if (b.label === 'United States') {
        return 1;
      }

      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
}
